import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import momentTimezone from "moment-timezone";
import SimpleModal from "../../SimpleModal";
import { client } from "../../../index";
import {
  getReUseTable,
  useHandleSectionSelect,
  fetchCaseReuseData,
  buildInitialValues,
  filterOnSearchTerm,
  updateValidSections,
} from "./helpers";
import { GET_PAGINATED_CASES_REUSE } from "../../../components/Forms/genericQueries";
import AlertOFLC from "../../../global/containers/AlertOFLC";
import FullScreenSpinner from "../../../global/components/FullScreenSpinner";
import { FaSpinner } from "react-icons/fa";
import { ReactTable } from "../ReactTable";
import { useDebounce } from "../../Hooks";
import UISchemaResolver from "../../../data/schema/form_ui/_resolver";
/**
 * Reuse: modal handler for case reuse
 * @param {boolean} open: is the reuse modal open or not
 * @param {function} toggleReuse: toggle the open-ness of the reuse modal
 * @param {string} formType: the 'id' of the form eg. 790A
 * @param {function} startNew: call to start a new form
 * @param {object} queryParameters: param object to grab case reuse data
 * @param {array} validSections: array of valid sections to reuse
 */

const Reuse = ({
  open,
  toggleReuse,
  formType,
  startNew,
  queryParameters,
  validSections,
  reuseVersion,
}) => {
  const [page, setPage] = useState(0);
  const [tablePage, setTablePage] = useState(0);
  const [selectedCase, setSelectedCase] = useState(null);
  const [paginatedCasesForReuse, setPaginatedCasesForReuse] = useState([]);
  const [casesForReuseLoading, setCasesForReuseLoading] = useState(false);
  const [
    selectedSections,
    toggleSection,
    resetSelectedSections,
    toggleSelectAllSections,
    allSelected,
  ] = useHandleSectionSelect([]);
  const [searchTerm, setSearchTerm] = useState(undefined);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [formVersionId, setFormVersionId] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const showRefYearMessage = ["9141"].includes(formType);
  const { form_id, user_id, group_id, group_role } = queryParameters;

  const debouncedSearchTerm = useDebounce(searchTerm);

  let variables,
    gqlQuery,
    gqlResponseName,
    defaultSortById,
    columnFieldNames,
    paginated;
  if (form_id === "9141") {
    variables = {
      formId: form_id,
      formVersionId: [2, 3], // needs to be an array
      UISorting: {
        type: "Reuse",
        caseType: "9141",
        limit: 5,
        page: tablePage,
        sort_ascending: 1,
        sort_column: "dateSubmitted",
      },
      UISearch: { search: "" },
    };
    gqlQuery = GET_PAGINATED_CASES_REUSE;
    gqlResponseName = "getPaginatedDatahubCasesForReuse";
    paginated = true;
    defaultSortById = "dateSubmitted";
    columnFieldNames = {
      caseNumber: "caseNumber",
      employer: "empBusinessName",
      socTitle: "suggestedSocTitle",
    };
  } else if (form_id === "9035") {
    variables = {
      formId: form_id,
      formVersionId: [1], // needs to be an array
      UISorting: {
        type: "Reuse",
        caseType: "9035",
        limit: 5,
        page: tablePage,
        sort_ascending: 1,
        sort_column: "dateSubmitted",
      },
      UISearch: { search: "" },
    };
    gqlQuery = GET_PAGINATED_CASES_REUSE;
    gqlResponseName = "getPaginatedDatahubCasesForReuse";
    paginated = true;
    defaultSortById = "submitDate";
    columnFieldNames = {
      caseNumber: "caseNumber",
      employer: "empBusinessName",
      socTitle: "tempneedSoctitle",
    };
  } else if (form_id === "790A") {
    variables = {
      formId: form_id,
      formVersionId: [reuseVersion], // needs to be an array
      UISorting: {
        type: "Reuse",
        caseType: "790A",
        limit: 5,
        page: tablePage,
        sort_ascending: 1,
        sort_column: "dateSubmitted",
      },
      UISearch: { search: "" },
    };
    gqlQuery = GET_PAGINATED_CASES_REUSE;
    gqlResponseName = "getPaginatedDatahubCasesForReuse";
    paginated = true;
    defaultSortById = "dateSubmitted";
    columnFieldNames = {
      caseNumber: "caseNumber",
      employer: "empBusinessName",
      socTitle: "jobTitle",
    };
  } else if (form_id === "9089") {
    variables = {
      formId: form_id,
      formVersionId: null,
      UISorting: {
        type: "Reuse",
        caseType: "9089",
        limit: 5,
        page: tablePage,
        sort_ascending: 1,
        sort_column: "dateSubmitted",
      },
      UISearch: { search: "" },
    };
    gqlQuery = GET_PAGINATED_CASES_REUSE;
    gqlResponseName = "getPaginatedDatahubCasesForReuse";
    defaultSortById = "dateSubmitted";
    paginated = true;
    columnFieldNames = {
      caseNumber: "caseNumber",
      employer: "empBusinessName",
      socTitle: "jobTitle",
    };
  }
  const { loading, error, data } = useQuery(gqlQuery, {
    variables,
    client,
    fetchPolicy: "network-only",
  });

  const fetchData = async ({ pageSize, pageIndex, sortBy, searchTerm }) => {
    setCasesForReuseLoading(true);
    variables.UISorting.page = pageIndex;
    variables.UISorting.sort_ascending = sortBy[0]?.desc ? 1 : 0;
    variables.UISorting.sort_column = sortBy[0]?.id;
    variables.UISearch.search = searchTerm;
    variables.UISorting.limit = pageSize;
    client
      .query({
        query: gqlQuery,
        variables: variables,
        fetchPolicy: "network-only",
      })
      .then((response) => {
        const { data, loading, error } = response;
        if (!error && !loading && data) {
          const dataTotalCount = data[gqlResponseName].total_count;
          setPaginatedCasesForReuse(data[gqlResponseName].ReusableCases);
          setPageCount(Math.ceil(dataTotalCount / pageSize));
          setCasesForReuseLoading(false);
        }
      });
  };

  const resetState = () => {
    setPage(0);
    setSearchTerm(undefined);
    toggleReuse();
    setSelectedCase(null);
    resetSelectedSections();
    setFormVersionId(null);
  };

  const handleSubmit = async () => {
    try {
      let response, data, errors;
      switch (formType) {
        case "9035":
        case "9141":
        case "9089":
        case "790A":
          response = await fetchCaseReuseData(
            selectedCase,
            formType,
            formVersionId
          );
          errors = response.errors;
          if (errors) {
            throw Error(errors[0].message);
          }
          data = response.data.reuseCase;
          break;
      }
      const initialValues = buildInitialValues(
        data,
        selectedSections,
        formType,
        reuseVersion
      );
      // For 9141, always add E.1 for H-2B even if the user unchecks section E for reuse
      if (formType === "9141" && initialValues?.visaType === "H-2B") {
        initialValues.coveredByAcwia = 2;
        initialValues.pre_dbaSca = 0;
        initialValues.altJobRequirement = 0;
      }
      const reuseMetadata = {
        case_number: selectedCase,
        disableSections: false,
        sections: UISchemaResolver(`${formType}-reuse-sections`)
          .filter((section) => selectedSections.includes(section.prefix))
          .map((section) => section.name),
      };

      setFetchLoading(false);
      startNew(initialValues, reuseVersion, reuseMetadata);
    } catch (e) {
      setFetchLoading(false);
      toggleReuse();
    }
  };

  let columns = useMemo(() => [
    {
      id: "radio",
      accessor: "",
      Cell: ({ row: { original, disabled } }) => {
        return (
          <>
            <input
              type="radio"
              name="pwd-selection"
              className="usa-radio-input"
              id={`${original[columnFieldNames.caseNumber]}`}
              onChange={(e) => e}
              checked={selectedCase === original[columnFieldNames.caseNumber]}
              onClick={() => {
                if (!original.disabled) {
                  setSelectedCase(original[columnFieldNames.caseNumber]);
                  // Only needed because of 790A V2 for getReUseTable(), clean this later
                  setFormVersionId(
                    formType === "790A" && original?.formVersionId === 2
                      ? original?.formVersionId
                      : null
                  );
                }
              }}
              value={original[columnFieldNames.caseNumber]}
              disabled={original.disabled}
            />
            <label
              className="usa-radio-label"
              htmlFor={`${original[columnFieldNames.caseNumber]}`}
              onChange={(e) => e}
              checked={selectedCase === original[columnFieldNames.caseNumber]}
              disabled={original.disabled}
            />
          </>
        );
      },
      disableSortBy: true,
      fixedWidth: 25,
    },
    {
      Header: "Case Number",
      id: columnFieldNames.caseNumber,
      accessor: (d) => d[columnFieldNames.caseNumber],
      Cell: (props) => <span>{props.value}</span>,
    },
    {
      Header: "Employer Name",
      id: columnFieldNames.employer,
      accessor: (d) => d[columnFieldNames.employer],
      Cell: (props) => <span>{props.value}</span>,
    },
    {
      Header: "FEIN",
      id: "empFein",
      accessor: (d) => d["empFein"],
      Cell: (props) => <span>{props.value}</span>,
      show: formType === "9035",
    },
    {
      Header: "Job Title",
      id: columnFieldNames.socTitle,
      accessor: (d) => d[columnFieldNames.socTitle],
      Cell: (props) => <span>{props.value}</span>,
    },
    {
      Header: "SOC",
      id: "soc",
      accessor: (d) => d["tempneedSoccode"],
      Cell: (props) => <span>{props.value}</span>,
      show: formType === "9035",
    },
    {
      Header: "Submitted Date",
      id: defaultSortById,
      accessor: (d) => d[defaultSortById],
      Cell: (props) => {
        let date;
        date = props.value
          ? momentTimezone(props.value)
              .tz("America/New_York")
              .format("MM/DD/YYYY") +
            " at " +
            momentTimezone(props.value).tz("America/New_York").format("h:mm A")
          : "--";
        return <span>{date}</span>;
      },
    },
    // {
    //   Header: "Status",
    //   id: "status",
    //   accessor: d => d["status"],
    //   Cell: props => <span>{props.value}</span>
    // }
  ]);

  if (!open) {
    return <div></div>;
  }

  let sectionColumns = [
    {
      Header: () => (
        <div>
          <input
            id="select_all_toggle"
            type="checkbox"
            className="usa-checkbox__input"
            onClick={() => {
              if (formVersionId === "1" && form_id === "790A") {
                validSections = updateValidSections();
              }
              return toggleSelectAllSections(validSections);
            }}
            checked={allSelected}
          />
          <label
            className="usa-checkbox__label"
            htmlFor="select_all_toggle"
            style={{
              margin: "3px 0",
            }}
          />
        </div>
      ),
      id: "checkbox",
      accessor: "",
      Cell: ({
        row: {
          original: { prefix },
        },
      }) => {
        return (
          <div>
            <input
              type="checkbox"
              className="usa-checkbox__input"
              id={`${prefix}`}
              onChange={(e) => e}
              checked={selectedSections && selectedSections.includes(prefix)}
              onClick={() => {
                toggleSection(prefix);
              }}
              value={prefix}
            />
            <label
              className="usa-checkbox__label"
              style={{ margin: "2px 0 0 8px" }}
              htmlFor={`${prefix}`}
              onChange={(e) => e}
              checked={selectedSections && selectedSections.includes(prefix)}
            />
          </div>
        );
      },
      disableSortBy: true,
      fixedWidth: 40,
    },
    {
      Header: "Section",
      id: "prefix",
      accessor: (d) => d["prefix"],
      Cell: (props) => <span>{props.value}</span>,
      minWidth: 120,
      maxWidth: 140,
      disableSortBy: true,
    },
    {
      Header: "Title",
      id: "name",
      accessor: (d) => d["name"],
      Cell: (props) => <span>{props.value}</span>,
      disableSortBy: true,
    },
  ];

  if (open && !loading && !error) {
    let casesForReuse = (data && data[gqlResponseName]) || [];

    if (!!paginated) {
      casesForReuse = casesForReuse.ReusableCases;
    }

    return (
      <>
        <SimpleModal
          toggleModal={toggleReuse}
          modalStatus={open && page === 0}
          renderButton={() => null}
          onRequestClose={() => resetState()}
        >
          <div className={"acknowledge-modal"}>
            <h3>
              <b>Case re-use?</b>
            </h3>
            <div className={"first-modal-text"}>
              {casesForReuse.length > 0 ? (
                <p>
                  Would you like to re-use sections from another {formType} or
                  start new?
                </p>
              ) : (
                <p>
                  You have no cases available to re-use. Please start a new
                  application.
                </p>
              )}

              <div className={"first-modal-buttons"}>
                <button
                  type={"button"}
                  className={"usa-button usa-button-outline-cancel"}
                  onClick={() => startNew(null, reuseVersion)}
                >
                  Start new
                </button>
                <button
                  type={"button"}
                  className={"usa-button usa-button-primary"}
                  disabled={casesForReuse.length === 0}
                  onClick={() => setPage(1)}
                  style={{ float: "right" }}
                >
                  Re-use
                </button>
              </div>
            </div>
          </div>
        </SimpleModal>
        <SimpleModal
          toggleModal={toggleReuse}
          modalStatus={page === 1}
          renderButton={() => null}
          minWidth="60%"
          onRequestClose={() => resetState()}
        >
          <div className={"case-lookup-heading"}>
            <h3>Case Reuse</h3>
          </div>
          <p>{`Locate the ${formType} form that you wish to re-use.`}</p>
          <div
            className={"job-order-search-form"}
            style={{
              display: "flex",
              alignContent: "center",
            }}
          >
            <input
              className="usa-input"
              style={{ marginTop: 0 }}
              type={"text"}
              placeholder={"Type search term here..."}
              id={"job-order-search"}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              style={{
                borderBottomLeftRadius: 0,
                borderTopLeftRadius: 0,
              }}
              type="button"
              className={"usa-button usa-button-primary"}
              onClick={(e) => e.preventDefault()}
            >
              Search
            </button>
            <button
              type="button"
              className={"reset-button"}
              onClick={(e) => {
                document.getElementById("job-order-search").value = "";
                setSearchTerm(undefined);
              }}
            >
              Reset
            </button>
          </div>
          {!paginated && (
            <ReactTable
              striped
              tableStyle={{ marginTop: 25 }}
              showPagination={true}
              defaultPageSize={5}
              pageSizeOptions={[5, 10, 25]}
              data={filterOnSearchTerm(casesForReuse, debouncedSearchTerm)}
              defaultSortBy={[
                {
                  id: defaultSortById,
                  desc: true,
                },
              ]}
              columns={columns}
            />
          )}

          {!!paginated && (
            <ReactTable
              striped
              tableStyle={{ marginTop: 25 }}
              loading={casesForReuseLoading}
              showPagination={true}
              data={paginatedCasesForReuse}
              fetchData={fetchData}
              pageCount={pageCount}
              searchTerm={debouncedSearchTerm}
              defaultPageSize={5}
              defaultSortBy={[
                {
                  id: defaultSortById,
                  desc: true,
                },
              ]}
              columns={columns}
              pageSizeOptions={[5, 10, 25]}
            />
          )}
          <button
            type="button"
            style={{ marginTop: "15px", float: "left" }}
            className={"usa-button usa-button--outline"}
            onClick={() => setPage(0)}
          >
            Back
          </button>
          <button
            className={"usa-button usa-button-primary modal-submit"}
            disabled={!selectedCase}
            type="button"
            onClick={() => setPage(2)}
          >
            Continue
          </button>
        </SimpleModal>
        <SimpleModal
          toggleModal={toggleReuse}
          modalStatus={page === 2}
          renderButton={() => null}
          minWidth="60%"
          onRequestClose={() => resetState()}
        >
          <div className={"acknowledge-modal"}>
            <h3>{`${formType} Case Reuse`}</h3>
            <div className={"acknowledge-modal-text"}>
              <p>
                <b>
                  Select Case Section(s) to re-use from selected case:{" "}
                  {selectedCase}
                </b>
              </p>
              {showRefYearMessage && (
                <AlertOFLC
                  type={"grey"}
                  messageStyle={{
                    fontSize: "14px",
                    lineHeight: "21px",
                  }}
                >
                  <b>Note:</b> County, SOC and NAICS values may not be
                  prepopulated for cases created in previous wage years.
                </AlertOFLC>
              )}
              <ReactTable
                striped
                defaultPageSize={30}
                data={getReUseTable(validSections, formType, formVersionId)}
                columns={sectionColumns}
              />
              <div>
                <button
                  type="button"
                  style={{ marginTop: "15px", float: "left" }}
                  className={"usa-button usa-button--outline"}
                  onClick={() => setPage(1)}
                >
                  Back
                </button>
                <button
                  type={"button"}
                  className={"usa-button usa-button-primary modal-submit"}
                  onClick={() => {
                    setFetchLoading(true);
                    handleSubmit();
                  }}
                  disabled={selectedSections && selectedSections.length < 1}
                >
                  Submit{" "}
                  <FaSpinner
                    style={{
                      width: "10px",
                      height: "10px",
                      marginleft: "8px",
                      transition: "width 400ms ease-in-out",
                      "-webkit-transition": "width 400ms ease-in-out",
                      "-moz-transition": "width 400ms ease-in-out",
                    }}
                    className={fetchLoading ? "icon-spin" : "hidden"}
                  />
                </button>
              </div>
            </div>
          </div>
        </SimpleModal>
      </>
    );
  } else {
    return <FullScreenSpinner />;
  }
};

export default Reuse;
